/* eslint-disable  */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { IoMdClose } from 'react-icons/io';
import classNames from 'classnames';
import ImageUploading from 'react-images-uploading';
import Modal from 'react-modal';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import clsx from 'clsx';

import APFormGroup from 'components/FormFields/APFormGroup';
import Box from 'components/Box';
import UploaderIcon from 'assets/images/uploader.svg';
import { styledTheme } from 'globalStyles/theme';
import './styles.scss';

const ImageCropper = ({ src, open, imageUploadType = 'photo', onClose, onSave, handleDelete, title = "Adjust your image.", imageUploadText = 'Drag your image to replace',
  showImageTypeRestrictionText = true, isImageSourceLoading = false, cropperConfig = null, sizeLimit = null }) => {

  const configureCropper = (optionalConfig = null) => {
    let config = {
      unit: '%',
      width: 50,
      x: 25,
      y: 25,
    };

    if (optionalConfig) {
      config = optionalConfig;
    }

    if (imageUploadType === 'photo') config = { ...config, aspect: 1 / 1 }
    else if (imageUploadType === 'shortcut') config = { ...config, aspect: 260 / 175 }
    else config = { ...config, height: 50 };

    return config;
  }

  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [imgSrc, setImageSource] = useState(src);
  const [imageType, setImageType] = useState(null);
  const [crop, setCrop] = useState(configureCropper(cropperConfig));
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imgBlob, setImgBlob] = useState(null);
  const [imgEncoded, setImgEncoded] = useState(null);
  const [isImageChanged, setImageChanged] = useState(false);
  const [isDraggingImage, setDraggingStatus] = useState(false);
  const [isImageInvalid, setImageInvalid] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const isSaveDisabled = isImageSourceLoading || imgSrc && (!completedCrop?.width || !completedCrop?.height)

  const onLoad = useCallback((img) => {
    imgRef.current = img;
  }, []);

  const encodeImage = (dataURI) => {
    let imageDataUrl = dataURI;
    const maxWidth = 560;
    const maxHeight = 560;

    const image = new Image();
    image.src = dataURI;
    image.onload = () => {
      let width = image.naturalWidth;
      let height = image.naturalHeight;
      if (width > maxWidth || height > maxHeight) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext('2d');
        const pixelRatio = window.devicePixelRatio;

        canvas.width = width * pixelRatio;
        canvas.height = height * pixelRatio;

        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(image, 0, 0, width, height);
        imageDataUrl = canvas.toDataURL(imageType, 1.0);
      };
      const binary = atob(imageDataUrl.split(',')[1]);
      const array = [];
      for (let i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i));
      }

      const bytes = new Uint8Array(array);
      setImgBlob(new Blob([bytes], { type: imageType }));

      let encoded = '';
      const len = bytes.length;
      for (let i = 0; i < len; ++i) {
        if (bytes[i] < 16) {
          encoded += '0' + bytes[i].toString(16);
        } else {
          encoded += bytes[i].toString(16);
        }
      }
      setImgEncoded(encoded);
    };
  };

  useEffect(() => {
    if (src) {
      setImageSource(src);
      encodeImage(src);
      setImageType(src.substring("data:".length, src.indexOf(";base64")))
    };
    setCrop(configureCropper(cropperConfig));
  }, [src]);

  useEffect(() => {
    if (!open) {
      setImageInvalid(false);
      setErrorMessage(null);
    }
  }, [open]);

  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * image.naturalWidth * pixelRatio / 100;
    canvas.height = crop.height * image.naturalHeight * pixelRatio / 100;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * image.naturalWidth / 100,
      crop.y * image.naturalHeight / 100,
      crop.width * image.naturalWidth / 100,
      crop.height * image.naturalHeight / 100,
      0,
      0,
      crop.width * image.naturalWidth / 100,
      crop.height * image.naturalHeight / 100,
    );
  }, [completedCrop]);

  const generateDownload = (canvas, crop) => {
    if (!crop || !canvas) {
      return;
    }
    const imageDataUrl = canvas.toDataURL(imageType, 1.0);
    canvas.toBlob(
      (blob) => {
        const reader = new FileReader();
        reader.addEventListener('loadend', (e) => {
          const buffer = e.currentTarget.result;
          const bytes = new Uint8Array(buffer);
          let encoded = '';
          const len = bytes.length;
          for (let i = 0; i < len; ++i) {
            if (bytes[i] < 16) {
              encoded += '0' + bytes[i].toString(16);
            } else {
              encoded += bytes[i].toString(16);
            }
          }
          onSave(imgSrc, imageDataUrl, blob, encoded, imgBlob, imgEncoded, isImageChanged, crop);
          onClose();
        });
        reader.readAsArrayBuffer(blob.slice(0, 8));
      },
      imageType,
      1
    );
  };

  const onChangeImage = async (imageList, addUpdateIndex) => {
    if (!addUpdateIndex) return;
    const newImg = imageList[addUpdateIndex[0]];
    const imgType = newImg.file.type;
    setImageType(imgType);
    setCrop(configureCropper(null));

    setImageInvalid(false);
    setErrorMessage(null);

    // setImageType
    setImageSource(newImg.data_url);
    encodeImage(newImg.data_url);
    setImageChanged(true);
  };

  const updateErrorMessage = (value) => {
    const { acceptType, maxFileSize } = value || {};
    if (acceptType) {
      setErrorMessage('Please upload your image in one of the following formats: JPEG or PNG.');
    }
    if (maxFileSize) {
      setErrorMessage(`Sorry, the image exceeds the maximum allowed size (${sizeLimit} MB).`);
    }
  };

  const renderImageUploadText = (onClick) => {
    return (
      <p className="form-description mt-2 pb-1">
        {imageUploadText}, or{' '}
        <span className="upload-button" onClick={onClick}>
          browse
        </span>
        . &nbsp;{showImageTypeRestrictionText && 'Jpeg or png images only.'}
      </p>
    );
  };

  return (
    <Modal
      ariaHideApp={false}
      isOpen={open}
      contentLabel={'PodoModal'}
      className={classNames('image-cropper', (imageUploadType === 'photo') ? 'photo-cropper' : '')}
      shouldCloseOnOverlayClick={false}
      onRequestClose={onClose}
      parentSelector={() => window.appRootMountPoint || document.body}
      style={{
        overlay: {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          padding: 0,
          backgroundColor: styledTheme.modalBackground,
          zIndex: 1000,
          inset: '-1px', // To fix weird border from overlaying elements in web component.
        },
      }}
    >
      <div>
        <div className="image-cropper__header" onClick={onClose}>
          <IoMdClose className="modal__close" size={24} onClick={onClose} />
        </div>
        <div className="image-cropper__content">
          <div className="mx-0">
            <section className="image-cropper__content-header">
              <h3>{title}</h3>
            </section>
          </div>
          <ImageUploading
            multiple={false}
            onChange={onChangeImage}
            dataURLKey="data_url"
            maxFileSize={sizeLimit ? sizeLimit * 1024 * 1024 : sizeLimit}
            acceptType={['jpg', 'jpeg', 'png']}
            onError={(errors) => {
              setImageInvalid(true);
              updateErrorMessage(errors);
            }}
          >
            {({ onImageUpload, isDragging, dragProps }) => (
              <div>
                <div {...dragProps}>
                  <div>
                    <div>
                      <section className={clsx('image-cropper__content-main')}
                        onDragEnter={(e) => {
                          dragProps.onDragEnter(e);
                          if (isDragging && !isDraggingImage) setDraggingStatus(true);
                        }}
                        onDragOver={(e) => {
                          dragProps.onDragOver(e);
                          setDraggingStatus(true);
                        }}
                        onDragLeave={(e) => {
                          dragProps.onDragLeave(e);
                          setDraggingStatus(false);
                        }}
                        onDrop={(e) => {
                          dragProps.onDrop(e);
                          setDraggingStatus(false);
                        }}
                      >
                        {imgSrc && (
                          <ReactCrop
                            src={imgSrc}
                            onImageLoaded={onLoad}
                            crop={crop}
                            onChange={(c) => setCrop(c)}
                            onComplete={(c, percentCrop) => {
                              setCompletedCrop(percentCrop);
                            }}
                          />
                        )}
                        {!imgSrc && (
                          <>
                            <img
                              src={UploaderIcon}
                              alt="uploader Icon"
                              className="upload-icon pb-1"
                            />
                            {renderImageUploadText(onImageUpload)}
                          </>)}
                        {isDraggingImage ? (
                          <div className="image-cropper__content-main__drag-overlay" />
                        ) : null}
                      </section>
                    </div>
                    <div className="image-cropper__content-cropped">
                      <canvas
                        ref={previewCanvasRef}
                        style={{
                          width: Math.round(completedCrop?.width ?? 0),
                          height: Math.round(completedCrop?.height ?? 0),
                          visibility: 'hidden',
                        }}
                      />
                    </div>
                    {imgSrc && (
                      <APFormGroup grid={true}>
                        {renderImageUploadText(onImageUpload)}
                        {handleDelete && (
                          <p className="form-description mt-2">
                            <span className="upload-button"
                              onClick={() => {
                                setImageSource(null);
                                setCrop(configureCropper(null));
                                setImageInvalid(false);
                                setErrorMessage(null);
                              }}
                            >
                              Remove current image
                            </span>
                          </p>
                        )}
                      </APFormGroup>
                    )}
                  </div>
                </div>
              </div>
            )}
          </ImageUploading>
          <div className="text-center my-2">
            <div
              className="image-cropper__content-error"
              style={{ color: isImageInvalid ? '#ab5243' : 'white' }}
            >
              {errorMessage}
            </div>
          </div>
          <div>
            <div>
              <Box>
                <button
                  className="image-cropper__buttons image-cropper__buttons--cancel"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  className="image-cropper__buttons"
                  disabled={isSaveDisabled}
                  onClick={() => {
                    if (imgSrc) {
                      generateDownload(previewCanvasRef.current, completedCrop)
                    } else {
                      handleDelete();
                      onClose();
                    }
                  }
                  }
                >
                  Save
                </button>
              </Box>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

ImageCropper.propTypes = {
  src: PropTypes.any,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  handleDelete: PropTypes.func,
  title: PropTypes.string,
  showImageTypeRestrictionText: PropTypes.bool,
  isImageSourceLoading: PropTypes.bool,
  imageUploadText: PropTypes.string,
  sizeLimit: PropTypes.number,
};

ImageCropper.defaultProps = {};

export default ImageCropper;
